import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, message, Select, Input, Form, Row, Col, DatePicker, Switch } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { clsx } from "clsx";
import { Tag } from "../../design-system";
import { SvgIcon } from "../../common/components/SvgIcon";
import {
    IhgBriefType,
    IhgEvaluationGoal,
    IhgRegion,
    IHG_EVALUATION_GOAL_LABELS,
    IHG_REGION_LABELS,
    IHG_SPEND_BANDS,
} from "./IhgDemoTypes";
import { IHG_TAXONOMY, findIhgCategory, ihgCategoryDisplay } from "./ihgTaxonomy";
import {
    BENCHMARKING_TOPICS,
    PRICING_TOPICS,
    FINANCIAL_TOPICS,
    MARKET_TOPICS,
    TYPE_TOPICS,
    DEEP_DIVE_TOPICS,
    DEEP_DIVE_IDS,
    GOAL_RECOMMENDATIONS,
    getTopicsForCategory,
    IhgTopicOption,
    OUTPUT_MODULES,
    DEFAULT_MODULE_IDS,
} from "./ihgTopicConfig";
import { IhgBriefInput } from "./ihgDemoData";
import { IhgTopicCard } from "./components/IhgTopicCard";
import styles from "./IhgBriefWizard.module.less";

interface Props {
    open: boolean;
    onClose: () => void;
    onComplete: (input: IhgBriefInput) => void;
}

const SUPPLIER_STEP_LABELS = ["Brief scope", "Evaluation goal", "Research focus", "Review & confirm"];
const CATEGORY_STEP_LABELS = ["Category scope", "Output modules", "Review & confirm"];

const DEFAULT_REGIONS = [IhgRegion.NORTH_AMERICA, IhgRegion.EMEA];
const DEFAULT_SUPPLIERS = ["Aspire", "impact.com", "Upfluence", "CreatorIQ", "Grin"];

const initialFormData = () => ({
    briefType: IhgBriefType.SUPPLIER,
    supplierName: "",
    supplierWebsite: "",
    categoryId: undefined as string | undefined,
    regions: [...DEFAULT_REGIONS] as IhgRegion[],
    spendBand: undefined as string | undefined,
    contractEndDate: "",
    incumbent: false,
    evaluationGoal: undefined as IhgEvaluationGoal | undefined,
    selectedTopics: [] as string[],
    suppliers: [...DEFAULT_SUPPLIERS] as string[],
    selectedModules: [...DEFAULT_MODULE_IDS] as string[],
});

const IhgBriefWizard: React.FC<Props> = ({ open, onClose, onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState(initialFormData);

    const isCategoryPath = formData.briefType === IhgBriefType.CATEGORY_BENCHMARK;
    const stepLabels = isCategoryPath ? CATEGORY_STEP_LABELS : SUPPLIER_STEP_LABELS;

    const visibleTopics = useMemo(
        () => getTopicsForCategory(formData.categoryId),
        [formData.categoryId]
    );

    const goalRecommendation = formData.evaluationGoal
        ? GOAL_RECOMMENDATIONS[formData.evaluationGoal]
        : null;
    const evaluationLabel = formData.evaluationGoal
        ? IHG_EVALUATION_GOAL_LABELS[formData.evaluationGoal]
        : "";

    // Reconcile selected topics when goal or category changes (same behavior as
    // the shipped wizard): prune topics no longer visible, re-apply goal preset.
    useEffect(() => {
        const visibleIds = new Set(visibleTopics.map((t) => t.id));
        const recommendation = formData.evaluationGoal
            ? GOAL_RECOMMENDATIONS[formData.evaluationGoal]
            : null;
        setFormData((prev) => {
            const topics = new Set(
                prev.selectedTopics.filter(
                    (id) => visibleIds.has(id) || DEEP_DIVE_IDS.includes(id)
                )
            );
            if (recommendation) {
                recommendation.on.forEach((id) => {
                    if (visibleIds.has(id) && !DEEP_DIVE_IDS.includes(id)) {
                        topics.add(id);
                    }
                });
                recommendation.off.forEach((id) => {
                    if (visibleIds.has(id)) {
                        topics.delete(id);
                    }
                });
            }
            return { ...prev, selectedTopics: Array.from(topics) };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.evaluationGoal, formData.categoryId]);

    const updateField = <K extends keyof ReturnType<typeof initialFormData>>(
        field: K,
        value: ReturnType<typeof initialFormData>[K]
    ) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const toggleRegion = (region: IhgRegion) => {
        setFormData((prev) => {
            const regions = new Set(prev.regions);
            if (regions.has(region)) {
                regions.delete(region);
            } else {
                regions.add(region);
            }
            return { ...prev, regions: Array.from(regions) };
        });
    };

    const toggleTopic = (topicId: string) => {
        setFormData((prev) => {
            const topics = new Set(prev.selectedTopics);
            if (topics.has(topicId)) {
                topics.delete(topicId);
            } else {
                topics.add(topicId);
            }
            return { ...prev, selectedTopics: Array.from(topics) };
        });
    };

    const toggleModule = (moduleId: string) => {
        setFormData((prev) => {
            const modules = new Set(prev.selectedModules);
            if (modules.has(moduleId)) {
                modules.delete(moduleId);
            } else {
                modules.add(moduleId);
            }
            return { ...prev, selectedModules: Array.from(modules) };
        });
    };

    const isTopicSelected = (topicId: string) => formData.selectedTopics.includes(topicId);
    const hasTopics = formData.selectedTopics.length > 0;

    const stepValid: boolean[] = isCategoryPath
        ? [
              Boolean(formData.categoryId),
              formData.selectedModules.length > 0,
              Boolean(formData.categoryId && formData.selectedModules.length > 0),
          ]
        : [
              Boolean(formData.supplierName.trim() && formData.categoryId),
              Boolean(formData.evaluationGoal),
              hasTopics,
              Boolean(
                  formData.supplierName.trim() &&
                      formData.categoryId &&
                      formData.evaluationGoal &&
                      hasTopics
              ),
          ];

    const goNext = () => setCurrentStep((s) => Math.min(s + 1, stepLabels.length - 1));
    const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

    const switchBriefType = (briefType: IhgBriefType) => {
        if (briefType === formData.briefType) return;
        updateField("briefType", briefType);
        setCurrentStep(0);
    };

    const handleSubmit = () => {
        const found = findIhgCategory(formData.categoryId);
        if (!found) {
            message.error("Please select a category.");
            return;
        }

        const input: IhgBriefInput = isCategoryPath
            ? {
                  briefType: IhgBriefType.CATEGORY_BENCHMARK,
                  subject: found.category.label,
                  category: found.group.label,
                  goalLabel: "Category Benchmark",
              }
            : {
                  briefType: IhgBriefType.SUPPLIER,
                  subject: formData.supplierName.trim(),
                  category: found.category.label,
                  goalLabel: formData.evaluationGoal
                      ? IHG_EVALUATION_GOAL_LABELS[formData.evaluationGoal]
                      : "",
              };

        message.success("Brief submitted. Research is running in the background.");
        handleReset();
        onComplete(input);
    };

    const handleReset = () => {
        setCurrentStep(0);
        setFormData(initialFormData());
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const categorySelectOptions = IHG_TAXONOMY.map((group) => ({
        label: group.label,
        options: group.categories.map((c) => ({ value: c.id, label: c.label })),
    }));

    const renderBriefTypeSelector = () => (
        <>
            <div className={styles.fieldLabel}>Brief type</div>
            <div className={styles.seg}>
                <div
                    className={clsx(styles.segOption, !isCategoryPath && styles.segOptionOn)}
                    onClick={() => switchBriefType(IhgBriefType.SUPPLIER)}
                >
                    Supplier Brief
                    <small>Diligence on one supplier</small>
                </div>
                <div
                    className={clsx(styles.segOption, isCategoryPath && styles.segOptionOn)}
                    onClick={() => switchBriefType(IhgBriefType.CATEGORY_BENCHMARK)}
                >
                    Category Benchmark
                    <small>Benchmark a market, multiple suppliers</small>
                </div>
            </div>
        </>
    );

    const renderRegionChips = () => (
        <div className={styles.chips}>
            {Object.values(IhgRegion).map((region) => (
                <span
                    key={region}
                    className={clsx(
                        styles.chip,
                        formData.regions.includes(region) && styles.chipOn
                    )}
                    onClick={() => toggleRegion(region)}
                >
                    {IHG_REGION_LABELS[region]}
                </span>
            ))}
        </div>
    );

    // ---- Supplier path steps ----

    const renderScopeStep = () => (
        <>
            <div className={styles.sectionTitle}>Brief scope</div>
            <div className={styles.sectionSub}>What are you evaluating?</div>

            {renderBriefTypeSelector()}

            <Form layout="vertical" style={{ marginTop: 8 }}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Supplier Name" required>
                            <Input
                                placeholder="e.g. Aspire"
                                value={formData.supplierName}
                                onChange={(e) => updateField("supplierName", e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Supplier Website">
                            <Input
                                placeholder="e.g. aspire.io"
                                value={formData.supplierWebsite}
                                onChange={(e) => updateField("supplierWebsite", e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <div className={clsx(styles.fieldLabel, styles.fieldRequired)}>Spend category</div>
            <Select
                style={{ width: "100%" }}
                placeholder="Select spend category"
                value={formData.categoryId}
                onChange={(value) => updateField("categoryId", value)}
                options={categorySelectOptions}
            />

            <div className={styles.fieldLabel}>Region scope</div>
            {renderRegionChips()}

            <div className={styles.fieldLabel}>
                Deal context
                <span className={styles.fieldHint}>optional, sharpens benchmark positioning</span>
            </div>
            <div className={styles.dealContext}>
                <div>
                    <div className={styles.fieldLabel} style={{ marginTop: 2 }}>
                        Annual spend
                    </div>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Select band"
                        allowClear
                        value={formData.spendBand}
                        onChange={(value) => updateField("spendBand", value)}
                        options={IHG_SPEND_BANDS.map((band) => ({ value: band, label: band }))}
                    />
                </div>
                <div>
                    <div className={styles.fieldLabel} style={{ marginTop: 2 }}>
                        Contract end date
                    </div>
                    <DatePicker
                        style={{ width: "100%" }}
                        onChange={(_, dateString) => updateField("contractEndDate", dateString)}
                    />
                </div>
                <div>
                    <div className={styles.fieldLabel} style={{ marginTop: 2 }}>
                        Incumbent supplier
                    </div>
                    <div className={styles.incumbentToggle}>
                        <Switch
                            checked={formData.incumbent}
                            onChange={(checked) => updateField("incumbent", checked)}
                        />
                        {formData.incumbent ? "Yes" : "No"}
                    </div>
                </div>
            </div>
        </>
    );

    const renderGoalStep = () => (
        <>
            <div className={styles.sectionTitle}>Evaluation Goal</div>
            <div className={styles.sectionSub}>
                What decision is this brief supporting? We'll recommend research topics to match.
            </div>
            <Form layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Evaluation Goal" required>
                            <Select
                                placeholder="Select evaluation goal"
                                value={formData.evaluationGoal}
                                onChange={(value) => updateField("evaluationGoal", value)}
                                options={Object.entries(IHG_EVALUATION_GOAL_LABELS).map(
                                    ([key, label]) => ({ value: key, label })
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            {goalRecommendation && (
                <div className={styles.goalBanner}>
                    Recommended for <strong>{evaluationLabel}</strong>:{" "}
                    {goalRecommendation.banner}
                </div>
            )}
        </>
    );

    const renderTopicSection = (title: string, topics: IhgTopicOption[]) => {
        if (topics.length === 0) return null;
        return (
            <>
                <div className={styles.topicCategory}>{title}</div>
                {topics.map((topic) => (
                    <IhgTopicCard
                        key={topic.id}
                        {...topic}
                        checked={isTopicSelected(topic.id)}
                        onChange={toggleTopic}
                    />
                ))}
            </>
        );
    };

    const renderTopicsStep = () => (
        <div>
            <div className={styles.sectionTitle}>Research Focus</div>
            <div className={styles.sectionSub}>
                We've pre-selected topics for your evaluation goal. Customize as needed.
            </div>

            {goalRecommendation && (
                <div className={styles.goalBanner}>
                    Recommended for <strong>{evaluationLabel}</strong>:{" "}
                    {goalRecommendation.banner}
                </div>
            )}

            <div className={styles.topicGrid}>
                {renderTopicSection(
                    "Benchmarking & Market Intelligence",
                    BENCHMARKING_TOPICS
                )}
                {renderTopicSection("Pricing & Commercial Terms", PRICING_TOPICS)}
                {renderTopicSection("Financial & Risk Intelligence", FINANCIAL_TOPICS)}
                {renderTopicSection("Market & Competitive Position", MARKET_TOPICS)}
                {formData.categoryId &&
                    renderTopicSection(
                        "Category-Specific",
                        TYPE_TOPICS.filter((t) =>
                            t.forCategories?.includes(formData.categoryId!)
                        )
                    )}
                {renderTopicSection("Optional Deep Dives", DEEP_DIVE_TOPICS)}
            </div>
        </div>
    );

    const reviewSections = (): {
        title: string;
        included: IhgTopicOption[];
        excluded: IhgTopicOption[];
    }[] => {
        const selected = new Set(formData.selectedTopics);
        const categoryTopics = formData.categoryId
            ? TYPE_TOPICS.filter((t) => t.forCategories?.includes(formData.categoryId!))
            : [];
        const sections: [string, IhgTopicOption[]][] = [
            ["Benchmarking & Market Intelligence", BENCHMARKING_TOPICS],
            ["Pricing & Commercial Terms", PRICING_TOPICS],
            ["Financial & Risk Intelligence", FINANCIAL_TOPICS],
            ["Market & Competitive Position", MARKET_TOPICS],
            ["Category-Specific", categoryTopics],
            ["Optional Deep Dives", DEEP_DIVE_TOPICS],
        ];
        return sections
            .filter(([, topics]) => topics.length > 0)
            .map(([title, topics]) => ({
                title,
                included: topics.filter((t) => selected.has(t.id)),
                excluded: topics.filter((t) => !selected.has(t.id)),
            }));
    };

    const renderFact = (label: string, value: React.ReactNode) => (
        <div className={styles.reviewFact}>
            <span className={styles.reviewFactLabel}>{label}</span>
            <span className={styles.reviewFactValue}>{value}</span>
        </div>
    );

    const regionsDisplay = formData.regions.map((r) => IHG_REGION_LABELS[r]).join(" · ") || "—";

    const renderSupplierReviewStep = () => (
        <div>
            <div className={styles.sectionTitle}>Review & Confirm</div>
            <div className={styles.sectionSub}>
                Check the supplier details and research scope before generating the brief.
            </div>

            <div className={styles.reviewSection}>
                <div className={styles.reviewSectionHeader}>
                    <span className={styles.topicCategory}>Brief scope</span>
                    <Button type="link" size="small" onClick={() => setCurrentStep(0)}>
                        Edit
                    </Button>
                </div>
                <div className={styles.reviewFacts}>
                    {renderFact("Supplier Name", formData.supplierName)}
                    {renderFact("Website", formData.supplierWebsite || "—")}
                    {renderFact("Spend Category", ihgCategoryDisplay(formData.categoryId))}
                    {renderFact("Region Scope", regionsDisplay)}
                    {renderFact("Annual Spend", formData.spendBand || "—")}
                    {renderFact("Contract End", formData.contractEndDate || "—")}
                    {renderFact("Incumbent", formData.incumbent ? "Yes" : "No")}
                </div>
            </div>

            <div className={styles.reviewSection}>
                <div className={styles.reviewSectionHeader}>
                    <span className={styles.topicCategory}>Evaluation Goal</span>
                    <Button type="link" size="small" onClick={() => setCurrentStep(1)}>
                        Edit
                    </Button>
                </div>
                <div className={styles.reviewFacts}>
                    {renderFact("Goal", evaluationLabel || "—")}
                </div>
            </div>

            <div className={styles.reviewSectionHeader}>
                <span className={styles.topicCategory}>Research Areas</span>
                <Button type="link" size="small" onClick={() => setCurrentStep(2)}>
                    Edit topics
                </Button>
            </div>
            {reviewSections().map((section) => (
                <div key={section.title} className={styles.reviewSection}>
                    <div className={styles.reviewSectionHeader}>
                        <span className={styles.reviewSectionTitle}>{section.title}</span>
                        <span className={styles.reviewCount}>
                            {section.included.length} of{" "}
                            {section.included.length + section.excluded.length} included
                        </span>
                    </div>
                    <div className={styles.reviewTagWrap}>
                        {section.included.map((topic) => (
                            <Tag
                                key={topic.id}
                                className={styles.tagIncluded}
                                icon={<CheckOutlined />}
                            >
                                {topic.label}
                            </Tag>
                        ))}
                        {section.excluded.map((topic) => (
                            <Tag
                                key={topic.id}
                                className={styles.tagExcluded}
                                icon={<CloseOutlined />}
                            >
                                {topic.label}
                            </Tag>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );

    // ---- Category Benchmark path steps ----

    const renderCategoryScopeStep = () => (
        <>
            <div className={styles.sectionTitle}>Category scope</div>
            <div className={styles.sectionSub}>
                Benchmark a market, not a single supplier. We research the category and every
                supplier in scope.
            </div>

            {renderBriefTypeSelector()}

            <div className={clsx(styles.fieldLabel, styles.fieldRequired)}>Category</div>
            <Select
                style={{ width: "100%" }}
                placeholder="Select category"
                value={formData.categoryId}
                onChange={(value) => updateField("categoryId", value)}
                options={categorySelectOptions}
            />

            <Row gutter={16}>
                <Col span={12}>
                    <div className={styles.fieldLabel}>Region scope</div>
                    {renderRegionChips()}
                </Col>
                <Col span={12}>
                    <div className={styles.fieldLabel}>Annual category spend</div>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Select band"
                        allowClear
                        value={formData.spendBand}
                        onChange={(value) => updateField("spendBand", value)}
                        options={IHG_SPEND_BANDS.map((band) => ({ value: band, label: band }))}
                    />
                </Col>
            </Row>

            <div className={styles.fieldLabel}>
                Suppliers to include
                <span className={styles.fieldHint}>
                    leave blank to auto-detect category leaders
                </span>
            </div>
            <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="+ Add supplier…"
                value={formData.suppliers}
                onChange={(value) => updateField("suppliers", value)}
                open={false}
            />
        </>
    );

    const renderModulesStep = () => (
        <div>
            <div className={styles.sectionTitle}>Output modules</div>
            <div className={styles.sectionSub}>
                Choose the sections this benchmark should research and render.
            </div>
            <div className={styles.topicGrid}>
                {OUTPUT_MODULES.map((module) => (
                    <IhgTopicCard
                        key={module.id}
                        id={module.id}
                        label={module.label}
                        description={module.description}
                        checked={formData.selectedModules.includes(module.id)}
                        onChange={toggleModule}
                    />
                ))}
            </div>
        </div>
    );

    const renderCategoryReviewStep = () => {
        const included = OUTPUT_MODULES.filter((m) => formData.selectedModules.includes(m.id));
        const excluded = OUTPUT_MODULES.filter((m) => !formData.selectedModules.includes(m.id));
        return (
            <div>
                <div className={styles.sectionTitle}>Review & Confirm</div>
                <div className={styles.sectionSub}>
                    Check the category scope and output modules before generating the benchmark.
                </div>

                <div className={styles.reviewSection}>
                    <div className={styles.reviewSectionHeader}>
                        <span className={styles.topicCategory}>Category scope</span>
                        <Button type="link" size="small" onClick={() => setCurrentStep(0)}>
                            Edit
                        </Button>
                    </div>
                    <div className={styles.reviewFacts}>
                        {renderFact("Category", ihgCategoryDisplay(formData.categoryId))}
                        {renderFact("Region Scope", regionsDisplay)}
                        {renderFact("Annual Category Spend", formData.spendBand || "—")}
                        {renderFact(
                            "Suppliers",
                            formData.suppliers.length > 0
                                ? formData.suppliers.join(", ")
                                : "Auto-detect category leaders"
                        )}
                    </div>
                </div>

                <div className={styles.reviewSectionHeader}>
                    <span className={styles.topicCategory}>Output modules</span>
                    <Button type="link" size="small" onClick={() => setCurrentStep(1)}>
                        Edit modules
                    </Button>
                </div>
                <div className={styles.reviewTagWrap}>
                    {included.map((m) => (
                        <Tag key={m.id} className={styles.tagIncluded} icon={<CheckOutlined />}>
                            {m.label}
                        </Tag>
                    ))}
                    {excluded.map((m) => (
                        <Tag key={m.id} className={styles.tagExcluded} icon={<CloseOutlined />}>
                            {m.label}
                        </Tag>
                    ))}
                </div>
            </div>
        );
    };

    const stepContent = isCategoryPath
        ? [renderCategoryScopeStep, renderModulesStep, renderCategoryReviewStep]
        : [renderScopeStep, renderGoalStep, renderTopicsStep, renderSupplierReviewStep];

    return (
        <Modal
            className={styles.wizardModal}
            open={open}
            onCancel={handleClose}
            title={isCategoryPath ? "New Buyer Brief · Category Benchmark" : "New Buyer Brief"}
            width={720}
            closeIcon={<SvgIcon name="close" size={14} />}
            footer={null}
            maskClosable={false}
            maskStyle={{
                background: "rgba(20, 30, 46, 0.95)",
                backdropFilter: "blur(25px)",
                top: "64px",
            }}
            destroyOnClose
        >
            <div className={styles.stepsBar}>
                {stepLabels.map((label, i) => (
                    <span
                        key={label}
                        className={
                            i < currentStep
                                ? styles.stepComplete
                                : i === currentStep
                                ? styles.stepCurrent
                                : styles.step
                        }
                    >
                        {i + 1}. {label}
                    </span>
                ))}
            </div>

            <div className={styles.stepContent}>{stepContent[currentStep]()}</div>

            <div className={styles.footer}>
                {currentStep === 0 ? (
                    <Button type="link" onClick={handleClose}>
                        Cancel
                    </Button>
                ) : (
                    <Button type="link" onClick={goBack}>
                        Back
                    </Button>
                )}
                {currentStep < stepLabels.length - 1 ? (
                    <Button type="primary" disabled={!stepValid[currentStep]} onClick={goNext}>
                        Next
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        disabled={!stepValid[currentStep]}
                        onClick={handleSubmit}
                    >
                        Generate Brief
                    </Button>
                )}
            </div>
        </Modal>
    );
};

export default IhgBriefWizard;
